import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Satellite as SatelliteIcon,
  Battery20 as BatteryLowIcon,
  Battery60 as BatteryMedIcon,
  BatteryFull as BatteryFullIcon,
  Speed as SpeedIcon,
  Height as HeightIcon,
  Explore as ExploreIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays } from 'date-fns';
import { it } from 'date-fns/locale';

import {
  DisplacementChart,
  TotalDisplacementChart,
  VelocityChart,
  CoordinatesChart,
} from '../components/TimeSeriesChart';
import {
  sensorsService,
  displacementsService,
  measurementsService,
} from '../services/api';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function SensorDetail() {
  const { sensorId } = useParams();
  const navigate = useNavigate();

  const [sensor, setSensor] = useState(null);
  const [displacement, setDisplacement] = useState(null);
  const [displacementSeries, setDisplacementSeries] = useState(null);
  const [positionSeries, setPositionSeries] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('30');

  const chartHeight = 280;

  // Carica dati
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [sensorData, displData, displSeries, posSeries, trendData] = await Promise.all([
          sensorsService.getById(sensorId),
          displacementsService.getLatest(sensorId).catch(() => null),
          displacementsService.getTimeSeries(sensorId, {
            start: subDays(new Date(), parseInt(period)).toISOString(),
          }),
          measurementsService.getTimeSeries(sensorId, {
            resolution: 'hourly',
            start: subDays(new Date(), parseInt(period)).toISOString(),
          }),
          displacementsService.getTrend(sensorId, parseInt(period)).catch(() => null),
        ]);

        setSensor(sensorData);
        setDisplacement(displData);
        setDisplacementSeries(displSeries);
        setPositionSeries(posSeries);
        setTrend(trendData);
      } catch (error) {
        console.error('Error loading sensor data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sensorId, period]);

  const getBatteryIcon = (level) => {
    if (level > 60) return <BatteryFullIcon color="success" sx={{ fontSize: 32 }} />;
    if (level > 30) return <BatteryMedIcon color="warning" sx={{ fontSize: 32 }} />;
    return <BatteryLowIcon color="error" sx={{ fontSize: 32 }} />;
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    const colors = {
      online: 'success',
      offline: 'default',
      warning: 'warning',
      alarm: 'error',
    };
    return colors[statusLower] || 'default';
  };

  const formatDisplacement = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 1000).toFixed(2)} mm`;
  };

  const formatVelocity = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 1000 * 365.25).toFixed(1)} mm/anno`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sensor) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Sensore non trovato</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Torna alla dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pt: 8, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate('/')}>
          <ArrowBackIcon />
        </IconButton>
        <SatelliteIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">
          {sensor.name}
        </Typography>
        <Chip
          label={sensor.status?.toUpperCase() || 'UNKNOWN'}
          color={getStatusColor(sensor.status)}
          size="small"
        />
      </Box>

      {/* Card spostamento e trend affiancate orizzontalmente */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Card spostamento attuale */}
        {displacement && (
          <Grid item xs={12} md={trend ? 6 : 12}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  Spostamento dal riferimento
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExploreIcon sx={{ transform: 'rotate(-45deg)', color: '#4caf50', fontSize: 18 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Nord</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatDisplacement(displacement.delta_north)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExploreIcon sx={{ transform: 'rotate(45deg)', color: '#2196f3', fontSize: 18 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Est</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatDisplacement(displacement.delta_east)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HeightIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Verticale</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatDisplacement(displacement.delta_up)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SpeedIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Totale 3D</Typography>
                        <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.85rem' }}>
                          {formatDisplacement(displacement.total)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Velocità (trend)</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {trend ? formatVelocity(trend.total_velocity) : 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Direzione</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {displacement.azimuth?.toFixed(1) || 'N/A'}°
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Trend stimato */}
        {trend && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  Trend stimato ({period >= 365 ? `${Math.round(period / 365)}a` : `${period}g`}) - {trend.samples} campioni
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExploreIcon sx={{ transform: 'rotate(-45deg)', color: '#4caf50', fontSize: 14 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>N vel</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatVelocity(trend.avg_velocity_north)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExploreIcon sx={{ transform: 'rotate(45deg)', color: '#2196f3', fontSize: 14 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>E vel</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatVelocity(trend.avg_velocity_east)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HeightIcon sx={{ color: '#ff9800', fontSize: 14 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>U vel</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {formatVelocity(trend.avg_velocity_up)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Vel. totale</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.85rem' }}>
                      {formatVelocity(trend.total_velocity)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Direzione</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {trend.predominant_azimuth?.toFixed(1)}° N
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Proiez. 30g</Typography>
                    <Typography variant="body2" fontWeight={500} color="primary" sx={{ fontSize: '0.85rem' }}>
                      {formatDisplacement(trend.projected_displacement_30d?.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Grafici a larghezza piena */}
      <Paper sx={{ p: 1.5 }}>
            {/* Controlli periodo */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={500}>Serie temporali</Typography>
              </Box>
              <ToggleButtonGroup
                value={period}
                exclusive
                onChange={(e, val) => val && setPeriod(val)}
                size="small"
              >
                <ToggleButton value="1" sx={{ px: 1, py: 0.3 }}>1g</ToggleButton>
                <ToggleButton value="7" sx={{ px: 1, py: 0.3 }}>7g</ToggleButton>
                <ToggleButton value="30" sx={{ px: 1, py: 0.3 }}>30g</ToggleButton>
                <ToggleButton value="90" sx={{ px: 1, py: 0.3 }}>90g</ToggleButton>
                <ToggleButton value="365" sx={{ px: 1, py: 0.3 }}>1a</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Tabs grafici */}
            <Tabs
              value={tabValue}
              onChange={(e, val) => setTabValue(val)}
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 32 }}
              TabIndicatorProps={{ sx: { height: 2 } }}
            >
              <Tab label="Spostamenti N/E/U" sx={{ minHeight: 32, py: 0.3, fontSize: '0.8rem' }} />
              <Tab label="Spost. totale" sx={{ minHeight: 32, py: 0.3, fontSize: '0.8rem' }} />
              <Tab label="Velocità" sx={{ minHeight: 32, py: 0.3, fontSize: '0.8rem' }} />
              <Tab label="Coordinate" sx={{ minHeight: 32, py: 0.3, fontSize: '0.8rem' }} />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <DisplacementChart data={displacementSeries} trend={trend} height={chartHeight} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TotalDisplacementChart data={displacementSeries} height={chartHeight} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <VelocityChart data={displacementSeries} height={chartHeight} />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <CoordinatesChart data={positionSeries} type="altitude" height={chartHeight * 0.5} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CoordinatesChart data={positionSeries} type="latitude" height={chartHeight * 0.5} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CoordinatesChart data={positionSeries} type="longitude" height={chartHeight * 0.5} />
                </Grid>
              </Grid>
            </TabPanel>
          </Paper>
    </Box>
  );
}

export default SensorDetail;
